import { createContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@/lib/types';
import { api } from '@/lib/api';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, pin: string) => Promise<void>;
  loginWithPassword: (email: string, password: string) => Promise<void>;
  loginWithPin: (email: string, pin: string) => Promise<void>;
  requestPin: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const userData = await api.get<User>('/auth/me');
      setUser(userData);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const requestPin = async (email: string) => {
    await api.post('/auth/request-pin', { email });
  };

  const login = async (email: string, pin: string) => {
    const userData = await api.post<User>('/auth/verify-pin', { email, pin });
    setUser(userData);
  };

  const loginWithPassword = async (email: string, password: string) => {
    const userData = await api.post<User>('/auth/login', { email, password });
    setUser(userData);
  };

  const loginWithPin = async (email: string, pin: string) => {
    const userData = await api.post<User>('/auth/login-with-pin', { email, pin });
    setUser(userData);
  };

  const logout = async () => {
    await api.post('/auth/logout');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, loginWithPassword, loginWithPin, requestPin, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};
